import { useMemo } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, Form, FormField, Button, Switch, useZodForm } from '@noor/ui';
import { InfoIcon } from 'lucide-react';
import { getApiErrorMessage, getApiFieldErrors } from '@noor/api';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import {
  toWorkingHoursSlots,
  fromWorkingHoursSlots,
  useVenueForEdit,
} from '@/entities/venue';
import { buildVenueHoursFormSchema, type VenueHoursFormValues } from '../model/schema';
import { useUpdateVenueHours } from '../model/use-update-venue-hours';

/** White bordered field pill — mirrors the promotion modal's field style. */
const FIELD =
  'rounded-3xl border-input bg-card shadow-xs focus-visible:border-input-border-focus';

/** Occupy space only when a validation message actually renders. */
const MESSAGE = 'min-h-0 px-1 empty:hidden';

export function VenueHoursModal({
  payload,
  close,
}: ModalRenderProps<'edit-venue-hours'>) {
  const { t } = useTranslation('eats');
  // The branch the screen is showing — see the payload schema. Absent on the
  // active-branch route, where the hook falls back to the session's branch.
  const update = useUpdateVenueHours(payload.branchId);

  // Re-read the branch on open rather than trusting the hours the card was
  // rendering. The payload is a snapshot taken when the modal was opened, and
  // the schedule is moderated — so a queued change approved since the page
  // loaded would otherwise be invisible here, and saving would write the
  // pre-approval hours straight back over it.
  const { data: venue, dataUpdatedAt } = useVenueForEdit(payload.branchId);

  const schema = useMemo(() => buildVenueHoursFormSchema(t), [t]);
  const buildValues = (): VenueHoursFormValues => ({
    days: toWorkingHoursSlots(venue?.workingHours ?? payload.workingHours),
  });
  const form = useZodForm(schema, {
    // The card's snapshot fills the fields immediately; the fresh read sharpens
    // them when it lands, so the modal is never blank mid-request.
    defaultValues: buildValues(),
    mode: 'onSubmit',
  });

  // The modal host unmounts this on close, so `open` is always true here — the
  // re-seed exists to catch the fresh read resolving.
  const { reset } = form;
  useFormSeed(true, payload.branchId ?? '', dataUpdatedAt, () => reset(buildValues()));
  const { fields } = useFieldArray({ control: form.control, name: 'days' });

  function handleSubmit(values: VenueHoursFormValues): void {
    update.mutate(
      { workingHours: fromWorkingHoursSlots(values.days) },
      {
        onSuccess: ({ pending }) => {
          // A gated merchant's schedule change is QUEUED for review rather
          // than applied — the screen keeps showing the stored hours until a
          // moderator approves it, so the queued case says so.
          toast.success(
            pending ? t('venue.hoursForm.pendingReview') : t('venue.hoursForm.success'),
          );
          close();
        },
        // The backend currently refuses schedule changes on a live branch
        // outright (`schedule changes are not yet supported on a moderated
        // (post-launch) restaurant update`, code 4104) — surface its reason
        // instead of a generic "couldn't save" so the merchant knows the
        // limitation is server-side.
        onError: (error) => {
          const { reason } = getApiFieldErrors(error);
          toast.error(getApiErrorMessage(error, reason ?? t('venue.hoursForm.error')));
        },
      },
    );
  }

  return (
    <Modal
      title={
        <span className="text-[1.375rem] font-semibold tracking-tight text-foreground">
          {t('venue.hoursForm.title')}
        </span>
      }
      description={<span className="sr-only">{t('venue.hoursForm.title')}</span>}
      className="gap-8 rounded-3xl p-6 sm:max-w-[30rem]"
      dismissible={!update.isPending}
    >
      <Form form={form} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
          <InfoIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-info" />
          {t('branches.form.orderWindowHint')}
        </p>
        <div className="flex flex-col gap-3">
          {fields.map((field, index) => (
            <DayRow
              key={field.id}
              form={form}
              index={index}
              label={t(`venue.weekdays.${field.label}`)}
            />
          ))}
        </div>

        <Button
          type="submit"
          size="lg"
          className="h-14 w-full rounded-full text-base"
          disabled={update.isPending}
        >
          {t('common:actions.save')}
        </Button>
      </Form>
    </Modal>
  );
}

interface DayRowProps {
  form: UseFormReturn<VenueHoursFormValues>;
  index: number;
  label: string;
}

/**
 * One weekday row: a fixed weekday label, an open/closed switch, and either the
 * open/close time pickers (open) or a "closed" note (closed). Toggling watches
 * `isOpen` so the controls swap live without unmounting the form fields.
 */
function DayRow({ form, index, label }: DayRowProps) {
  const { t } = useTranslation('eats');
  const isOpen = form.watch(`days.${index}.isOpen`);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-3">
        <div className="flex w-20 shrink-0 items-center gap-2 pt-2.5">
          <Switch
            checked={isOpen}
            onCheckedChange={(checked) =>
              form.setValue(`days.${index}.isOpen`, checked, { shouldValidate: true })
            }
            aria-label={t('venue.hoursForm.toggleLabel', { day: label })}
          />
          <span className="text-sm font-medium tracking-tight text-foreground">
            {label}
          </span>
        </div>

        {isOpen ? (
          <div className="flex flex-1 items-start gap-3">
            <FormField name={`days.${index}.open`} className="flex-1">
              <FormField.Label className="sr-only">
                {t('venue.hoursForm.openLabel', { day: label })}
              </FormField.Label>
              {/* Wall-clock hours may continue past midnight. */}
              <FormField.TimePicker className={FIELD} />
              <FormField.Message className={MESSAGE} />
            </FormField>
            <span className="pt-3 text-sm text-muted-foreground">—</span>
            <FormField name={`days.${index}.close`} className="flex-1">
              <FormField.Label className="sr-only">
                {t('venue.hoursForm.closeLabel', { day: label })}
              </FormField.Label>
              {/* 24:00 explicitly marks the end of this day. */}
              <FormField.TimePicker className={FIELD} allowEndOfDay allow24HourEnd />
              <FormField.Message className={MESSAGE} />
            </FormField>
          </div>
        ) : (
          <span className="flex-1 pt-3 text-sm text-muted-foreground">
            {t('venue.hoursForm.closed')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField name={`days.${index}.orderFrom`}>
          <FormField.Label className="text-xs">
            {t('branches.form.orderFrom', { day: label })}
          </FormField.Label>
          <FormField.TimePicker disabled={!isOpen} placeholder="—" />
          <FormField.Message className={MESSAGE} />
        </FormField>
        <FormField name={`days.${index}.orderTo`}>
          <FormField.Label className="text-xs">
            {t('branches.form.orderTo', { day: label })}
          </FormField.Label>
          <FormField.TimePicker
            disabled={!isOpen}
            allowEndOfDay
            allow24HourEnd
            placeholder="—"
          />
          <FormField.Message className={MESSAGE} />
        </FormField>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        disabled={!isOpen}
        onClick={() => {
          form.setValue(`days.${index}.orderFrom`, '', {
            shouldDirty: true,
          });
          form.setValue(`days.${index}.orderTo`, '', { shouldDirty: true });
          void form.trigger(`days.${index}`);
        }}
      >
        {t('branches.form.orderAllDay')}
      </Button>
    </div>
  );
}
